import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaPlus, FaSync, FaCopy, FaBolt, FaShieldAlt, FaSearch,
  FaFilter, FaTimes, FaExclamationTriangle, FaCheckCircle,
  FaClock, FaCogs, FaListUl, FaServer, FaFileAlt,
  FaExchangeAlt, FaTimesCircle, FaQuestion, FaWrench,
  FaChevronDown,
} from 'react-icons/fa';
import './IntegracoesSeguradoras.css';
import { useSocket } from './SocketContext';

// ═══════════════════════════════════════════════════════════════
// CONSTANTES DE TIPOS DE EVENTO
// ═══════════════════════════════════════════════════════════════

const TIPOS_EVENTO = {
  chamado:      { label: 'Chamado → OS',       color: '#2563eb', bg: '#eff6ff', icon: FaFileAlt,       desc: 'Cria uma Ordem de Serviço automaticamente' },
  sinistro:     { label: 'Sinistro',            color: '#dc2626', bg: '#fef2f2', icon: FaExclamationTriangle, desc: 'Registra sinistro na base de dados' },
  apolice:      { label: 'Apólice',             color: '#16a34a', bg: '#f0fdf4', icon: FaShieldAlt,     desc: 'Persiste dados de apólice/prêmio' },
  status_update:{ label: 'Atualização Status',  color: '#ea580c', bg: '#fff7ed', icon: FaExchangeAlt,   desc: 'Atualiza status de OS ou sinistro' },
  cancelamento: { label: 'Cancelamento',        color: '#64748b', bg: '#f8fafc', icon: FaTimesCircle,   desc: 'Cancela OS ou sinistro existente' },
  consulta:     { label: 'Consulta',            color: '#8b5cf6', bg: '#f5f3ff', icon: FaSearch,        desc: 'Resposta síncrona — sem persistência' },
};

// ═══════════════════════════════════════════════════════════════
// UTILS
// ═══════════════════════════════════════════════════════════════

const pillClass = (v) => {
  const s = String(v || '').toLowerCase();
  if (['ativo','processado','ok','200','retorno_enviado','healthy','saudavel'].includes(s)) return 'pill ativo';
  if (['recebido','retorno_pendente','warning','atencao','pendente'].includes(s))           return 'pill recebido';
  if (['suspenso','off','false'].includes(s))                                               return 'pill suspenso';
  if (['erro','rejeitado','401','403','500','retorno_erro','critico','invalidado'].includes(s)) return 'pill rejeitado';
  if (['duplicado'].includes(s))                                                            return 'pill duplicado';
  return 'pill';
};

const tipoMeta = (tipo) => TIPOS_EVENTO[String(tipo||'').toLowerCase()] || {
  label: tipo || '—', color: '#64748b', bg: '#f8fafc', icon: FaQuestion, desc: 'Tipo desconhecido'
};

const dirLabel = (v) => {
  const s = String(v||'').toLowerCase();
  if (s === 'inbound')  return 'Entrada';
  if (s === 'outbound') return 'Saída';
  return '—';
};

const dirBadgeCls = (v) => {
  const s = String(v||'').toLowerCase();
  if (s === 'inbound')  return 'seg2-dirBadge inbound';
  if (s === 'outbound') return 'seg2-dirBadge outbound';
  return 'seg2-dirBadge';
};

const authLabel = (v) => {
  const s = String(v||'').toLowerCase();
  if (s === 'api_key') return 'API Key';
  if (s === 'hmac')    return 'HMAC';
  if (s === 'none')    return 'Sem auth';
  return v || '—';
};

const boolText = (v) => Number(v) === 1 || v === true ? 'Ativo' : 'Inativo';

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString('pt-BR'); } catch { return String(v); }
};

const relTime = (v) => {
  if (!v) return 'Sem tráfego';
  try {
    const d = Math.floor((Date.now() - new Date(v).getTime()) / 60000);
    if (d < 1) return 'Agora';
    if (d < 60) return `${d} min atrás`;
    const h = Math.floor(d / 60);
    if (h < 24) return `${h} h atrás`;
    return `${Math.floor(h / 24)} dia(s) atrás`;
  } catch { return String(v); }
};

const prettyJson = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') { try { return JSON.stringify(JSON.parse(v), null, 2); } catch { return v; } }
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
};

function JsonViewer({ value }) {
  return <pre className="seg-json">{prettyJson(value)}</pre>;
}

// ═══════════════════════════════════════════════════════════════
// API HELPERS
// ═══════════════════════════════════════════════════════════════

const tryAlt = (path) => String(path).startsWith('/api/')
  ? String(path).replace('/api', '')
  : `/api${String(path)}`;

async function safeGet(path)        { try { return await api.get(path);         } catch { return api.get(tryAlt(path));         } }
async function safePost(path, body) { try { return await api.post(path, body);  } catch { return api.post(tryAlt(path), body);  } }
async function safePut(path, body)  { try { return await api.put(path, body);   } catch { return api.put(tryAlt(path), body);   } }
async function safeDel(path)        { try { return await api.delete(path);       } catch { return api.delete(tryAlt(path));       } }

// ═══════════════════════════════════════════════════════════════
// FORM BASE
// ═══════════════════════════════════════════════════════════════

const emptySeg = {
  nome: '', slug: '', codigo_interno: '', adapter_key: '',
  status: 'ativo', tipo_auth: 'api_key', ambiente: 'teste',
  api_base_url: '', endpoint_outbound: '', metodo_outbound: 'POST',
  timeout_ms: 15000, retry_max_tentativas: 3, retry_intervalo_seg: 300,
  webhook_ativo: 1, inbound_ativo: 1, outbound_ativo: 1,
  email_ingest_ativo: 0, email_ingest_address: '',
  webhook_secret: '', outbound_secret: '',
  tipos_evento_aceitos: ['chamado','sinistro','apolice','status_update','cancelamento','consulta'],
};

// ═══════════════════════════════════════════════════════════════
// BADGE DE TIPO DE EVENTO
// ═══════════════════════════════════════════════════════════════

function TipoBadge({ tipo }) {
  const m = tipoMeta(tipo);
  const Icon = m.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 8px', borderRadius: 8, fontSize: 11, fontWeight: 800,
      background: m.bg, color: m.color, whiteSpace: 'nowrap',
    }}>
      <Icon style={{ fontSize: 10 }} />
      {m.label}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// PAINEL OPERACIONAL — CARD DA SEGURADORA
// ═══════════════════════════════════════════════════════════════

function PartnerCard({ seg, stat, isAdminGeral, onTest, onEdit, onToggle, onCopy, webhookBase, sinistrosRecebidos, apolicesRecebidas }) {
  const [expanded, setExpanded] = useState(false);
  const [testTipo, setTestTipo] = useState('chamado');

  const health =
    String(seg.status||'').toLowerCase() !== 'ativo'               ? { label: 'Suspenso', cls: 'suspenso' } :
    !Number(seg.inbound_ativo) && !Number(seg.outbound_ativo)      ? { label: 'Parado',   cls: 'suspenso' } :
    (stat.erro||0) + (stat.rejeitado||0) > 0                       ? { label: 'Atenção',  cls: 'recebido' } :
                                                                      { label: 'Saudável', cls: 'ativo'    };

  // Tipos aceitos
  let tiposAceitos = [];
  try {
    tiposAceitos = typeof seg.tipos_evento_aceitos === 'string'
      ? JSON.parse(seg.tipos_evento_aceitos)
      : (seg.tipos_evento_aceitos || []);
  } catch { tiposAceitos = []; }

  // URLs disponíveis
  const urls = [
    { label: 'Evento (auto-detect)', path: `/api/public/seguradoras/${seg.slug}/evento` },
    { label: 'Webhook (legado / chamado)', path: `/api/public/seguradoras/${seg.slug}/webhook` },
    { label: 'Sinistro', path: `/api/public/seguradoras/${seg.slug}/sinistro` },
    { label: 'Apólice', path: `/api/public/seguradoras/${seg.slug}/apolice` },
  ];

  // Breakdown por tipo de evento
  const porTipo = {};
  Object.keys(TIPOS_EVENTO).forEach(t => { porTipo[t] = stat.porTipo?.[t] || 0; });

  return (
    <section className="seg2-partnerCard">
      {/* Cabeçalho */}
      <div className="seg2-partnerHead">
        <div>
          <div className="seg2-partnerTitleRow">
            <h3>{seg.nome}</h3>
            <span className={pillClass(health.cls)}>{health.label}</span>
            {seg.ambiente && <span className="seg2-environment">{seg.ambiente}</span>}
          </div>
          <div className="seg2-partnerMeta">
            <span>slug: <b>{seg.slug}</b></span>
            <span>adapter: <b>{seg.adapter_key || seg.slug}</b></span>
            <span>auth: <b>{authLabel(seg.tipo_auth)}</b></span>
          </div>
        </div>
        <div className="seg2-partnerHeadActions">
          {/* Teste com seleção de tipo */}
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            <select
              className="seg2-testSelect"
              value={testTipo}
              onChange={e => setTestTipo(e.target.value)}
              title="Tipo de evento a testar">
              {Object.entries(TIPOS_EVENTO).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <button className="seg2-miniBtn" onClick={() => onTest(seg.id, testTipo)}>
              <FaBolt /> Testar
            </button>
          </div>
          {isAdminGeral && (
            <button className="seg2-miniBtn seg2-miniBtnGhost" onClick={() => onEdit(seg)}>
              <FaCogs /> Config
            </button>
          )}
        </div>
      </div>

      {/* Status rápido */}
      <div className="seg2-quickStatusRow">
        <div className="seg2-quickStatusItem">
          <span className="seg2-quickLabel">Status</span>
          <span className={pillClass(seg.status)}>{seg.status}</span>
        </div>
        <div className="seg2-quickStatusItem">
          <span className="seg2-quickLabel">Entrada</span>
          <span className={pillClass(Number(seg.inbound_ativo) ? 'ativo' : 'suspenso')}>{boolText(seg.inbound_ativo)}</span>
        </div>
        <div className="seg2-quickStatusItem">
          <span className="seg2-quickLabel">Saída</span>
          <span className={pillClass(Number(seg.outbound_ativo) ? 'ativo' : 'suspenso')}>{boolText(seg.outbound_ativo)}</span>
        </div>
        <div className="seg2-quickStatusItem">
          <span className="seg2-quickLabel">Eventos</span>
          <span style={{ fontWeight:900, color:'#0f172a', fontSize:16 }}>{stat.total||0}</span>
        </div>
      </div>

      {/* Corpo */}
      <div className="seg2-partnerBody">
        {/* Estatísticas por tipo de evento */}
        <div className="seg2-tipoGrid">
          {Object.entries(TIPOS_EVENTO).map(([tipo, meta]) => {
            const Icon = meta.icon;
            const count = stat.porTipo?.[tipo] || 0;
            return (
              <div key={tipo} className="seg2-tipoStat" style={{ borderLeft: `3px solid ${meta.color}` }}>
                <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:3 }}>
                  <Icon style={{ color: meta.color, fontSize:10 }} />
                  <span style={{ fontSize:10, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.4 }}>
                    {meta.label}
                  </span>
                </div>
                <div style={{ fontSize:18, fontWeight:900, color: count > 0 ? meta.color : '#cbd5e1' }}>{count}</div>
              </div>
            );
          })}
        </div>

        {/* Dados recebidos: sinistros e apólices */}
        {(sinistrosRecebidos > 0 || apolicesRecebidas > 0) && (
          <div className="seg2-receivedRow">
            {sinistrosRecebidos > 0 && (
              <div className="seg2-receivedChip" style={{ background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca' }}>
                <FaExclamationTriangle style={{ fontSize:11 }} />
                {sinistrosRecebidos} sinistro{sinistrosRecebidos !== 1 ? 's' : ''} recebido{sinistrosRecebidos !== 1 ? 's' : ''}
              </div>
            )}
            {apolicesRecebidas > 0 && (
              <div className="seg2-receivedChip" style={{ background:'#f0fdf4', color:'#16a34a', border:'1px solid #bbf7d0' }}>
                <FaShieldAlt style={{ fontSize:11 }} />
                {apolicesRecebidas} apólice{apolicesRecebidas !== 1 ? 's' : ''} ativa{apolicesRecebidas !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}

        {/* URLs */}
        <div className="seg2-urlSection">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <span style={{ fontSize:11, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:0.5 }}>
              Endpoints Públicos
            </span>
            <button className="seg2-expandBtn" onClick={() => setExpanded(v => !v)}>
              {expanded ? 'Ocultar' : 'Ver todos'} <FaChevronDown style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition:'0.2s' }} />
            </button>
          </div>

          {/* URL principal sempre visível */}
          <div className="seg2-api-link-box full">
            <code>{webhookBase}/api/public/seguradoras/{seg.slug}/evento</code>
            <button onClick={() => onCopy(`${webhookBase}/api/public/seguradoras/${seg.slug}/evento`, 'URL copiada!')}>
              <FaCopy />
            </button>
          </div>

          {/* Demais URLs (expansível) */}
          {expanded && (
            <div style={{ display:'grid', gap:6, marginTop:6 }}>
              {urls.slice(1).map(u => (
                <div key={u.path} style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:10, fontWeight:700, color:'#94a3b8', minWidth:120 }}>{u.label}</span>
                  <div className="seg2-api-link-box full" style={{ flex:1 }}>
                    <code>{webhookBase}{u.path}</code>
                    <button onClick={() => onCopy(`${webhookBase}${u.path}`, 'URL copiada!')}>
                      <FaCopy />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tipos aceitos */}
        {tiposAceitos.length > 0 && (
          <div>
            <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:0.5, marginBottom:6 }}>
              Tipos aceitos
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
              {tiposAceitos.map(t => <TipoBadge key={t} tipo={t} />)}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="seg2-partnerTimeline">
          <div className="seg2-lineItem">
            <span className="seg2-lineLabel">Último tráfego</span>
            <strong>{stat.ultimoEvento ? fmtDate(stat.ultimoEvento) : 'Sem registros'}</strong>
          </div>
          <div className="seg2-lineItem">
            <span className="seg2-lineLabel">Último sucesso inbound</span>
            <strong>{fmtDate(seg.ultimo_sucesso_inbound_em)}</strong>
          </div>
          <div className="seg2-lineItem">
            <span className="seg2-lineLabel">Último erro</span>
            <strong style={{ color: seg.ultimo_erro_inbound_em ? '#dc2626' : '#94a3b8' }}>
              {fmtDate(seg.ultimo_erro_inbound_em)}
            </strong>
          </div>
        </div>
      </div>

      {/* Footer (admin) */}
      {isAdminGeral && (
        <div className="seg2-partnerFooter">
          <button className={`seg2-toggleBtn ${Number(seg.inbound_ativo) ? 'on' : 'off'}`}
            onClick={() => onToggle(seg, 'inbound_ativo')}>
            {Number(seg.inbound_ativo) ? 'Desativar Entrada' : 'Ativar Entrada'}
          </button>
          <button className={`seg2-toggleBtn ${Number(seg.outbound_ativo) ? 'on' : 'off'}`}
            onClick={() => onToggle(seg, 'outbound_ativo')}>
            {Number(seg.outbound_ativo) ? 'Desativar Saída' : 'Ativar Saída'}
          </button>
          <button className={`seg2-toggleBtn ${String(seg.status).toLowerCase() === 'ativo' ? 'on' : 'off'}`}
            onClick={() => onToggle(seg, 'status')}>
            {String(seg.status).toLowerCase() === 'ativo' ? 'Suspender' : 'Reativar'}
          </button>
        </div>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// DRAWER DE EVENTO
// ═══════════════════════════════════════════════════════════════

function EventDrawer({ ev, open, onClose, onReprocess }) {
  const podeReprocessar = () => {
    const dir    = String(ev?.direcao||'').toLowerCase();
    const status = String(ev?.status||'').toLowerCase();
    return dir === 'inbound' && ['erro','rejeitado'].includes(status);
  };

  return (
    <>
      <div className={`seg2-drawerBackdrop ${open ? 'open' : ''}`} onClick={onClose} />
      <aside className={`seg2-drawer ${open ? 'open' : ''}`}>
        <div className="seg2-drawerHeader">
          <div>
            <div className="seg2-drawerTitle">Evento #{ev?.id}</div>
            <div className="seg2-drawerSub">
              {ev?.seguradora_nome || '—'} · {dirLabel(ev?.direcao)}
            </div>
          </div>
          <button className="seg2-btn seg2-btnGhost" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="seg2-drawerBody">
          {/* Meta grid */}
          <div className="seg2-drawerMetaGrid">
            <div className="seg2-metaCard">
              <span>Status</span>
              <strong><span className={pillClass(ev?.status)}>{ev?.status||'—'}</span></strong>
            </div>
            <div className="seg2-metaCard">
              <span>Direção</span>
              <strong>{dirLabel(ev?.direcao)}</strong>
            </div>
            <div className="seg2-metaCard span2">
              <span>Tipo do Evento</span>
              <strong><TipoBadge tipo={ev?.tipo_evento} /></strong>
            </div>
            <div className="seg2-metaCard">
              <span>HTTP</span>
              <strong>{ev?.http_status||'—'}</strong>
            </div>
            <div className="seg2-metaCard">
              <span>Duração</span>
              <strong>{ev?.duracao_ms != null ? `${ev.duracao_ms}ms` : '—'}</strong>
            </div>
            <div className="seg2-metaCard">
              <span>Protocolo</span>
              <strong style={{ fontFamily:'monospace', fontSize:12 }}>{ev?.protocolo_externo||'—'}</strong>
            </div>
            <div className="seg2-metaCard">
              <span>Nº Sinistro</span>
              <strong style={{ fontFamily:'monospace', fontSize:12 }}>{ev?.numero_sinistro||'—'}</strong>
            </div>
            <div className="seg2-metaCard">
              <span>OS Vinculada</span>
              <strong>{ev?.os_id ? `#${ev.os_id}` : '—'}</strong>
            </div>
            <div className="seg2-metaCard">
              <span>Sinistro ID</span>
              <strong>{ev?.sinistro_id ? `#${ev.sinistro_id}` : '—'}</strong>
            </div>
            <div className="seg2-metaCard span2">
              <span>Recebido em</span>
              <strong>{fmtDate(ev?.recebido_em || ev?.created_at)}</strong>
            </div>
            <div className="seg2-metaCard span2">
              <span>Endpoint</span>
              <strong className="seg2-break" style={{ fontSize:11, fontFamily:'monospace' }}>{ev?.endpoint||'—'}</strong>
            </div>
          </div>

          {/* Erro */}
          {ev?.erro && (
            <div className="seg2-drawerErrorBox">
              <h3>Erro / Motivo</h3>
              <p><code style={{ fontSize:12 }}>{ev.erro_codigo && `[${ev.erro_codigo}] `}{ev.erro}</code></p>
            </div>
          )}

          {/* Reprocessar */}
          {podeReprocessar() && (
            <div className="seg2-drawerActionBox">
              <button className="seg2-btn" onClick={() => onReprocess(ev.id)}>
                <FaSync /> Reprocessar Evento
              </button>
              <p style={{ fontSize:12, color:'#64748b', margin:'8px 0 0' }}>
                Cria um novo evento com o mesmo payload. O evento original é mantido para auditoria.
              </p>
            </div>
          )}

          <div className="seg2-drawerSection"><h3>Payload recebido</h3><JsonViewer value={ev?.payload_json} /></div>
          <div className="seg2-drawerSection"><h3>Resposta / retorno</h3><JsonViewer value={ev?.response_json} /></div>
          <div className="seg2-drawerSection"><h3>Headers</h3><JsonViewer value={ev?.headers_json} /></div>
        </div>
      </aside>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL DE CONFIGURAÇÃO DE SEGURADORA
// ═══════════════════════════════════════════════════════════════

function SeguradoraModal({ open, editing, form, onChange, onSave, onClose, saving, onGenSecret, onCopy }) {
  if (!open) return null;

  const toggleTipo = (tipo) => {
    const atual = form.tipos_evento_aceitos || [];
    const next  = atual.includes(tipo) ? atual.filter(t => t !== tipo) : [...atual, tipo];
    onChange('tipos_evento_aceitos', next);
  };

  return (
    <div className="seg2-modalBackdrop" onClick={onClose}>
      <div className="seg2-modal xlarge" onClick={e => e.stopPropagation()}>
        <div className="seg2-modalHeader">
          <h2>{editing ? 'Configurar integração' : 'Nova integração'}</h2>
          <button className="seg2-btn seg2-btnGhost" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="seg2-modalBody">
          {/* 1. Identificação */}
          <div className="seg2-form-section">
            <h3>1. Identificação do parceiro</h3>
            <div className="seg2-formGrid">
              <div>
                <label>Nome fantasia</label>
                <input value={form.nome} onChange={e => onChange('nome', e.target.value)} placeholder="Ex: Porto Seguro" />
              </div>
              <div>
                <label>Slug (URL)</label>
                <input value={form.slug}
                  onChange={e => {
                    const slug = e.target.value.toLowerCase().replace(/\s+/g, '-');
                    onChange('slug', slug);
                    if (!form.adapter_key) onChange('adapter_key', slug.replace(/-/g, '_'));
                  }}
                  placeholder="ex: porto-seguro" />
              </div>
              <div>
                <label>Código interno</label>
                <input value={form.codigo_interno}
                  onChange={e => onChange('codigo_interno', e.target.value.toUpperCase())}
                  placeholder="Ex: PORTO_SEGURO" />
              </div>
              <div>
                <label>Adapter Key</label>
                <input value={form.adapter_key} onChange={e => onChange('adapter_key', e.target.value)} placeholder="Ex: porto_seguro" />
              </div>
              <div>
                <label>Status</label>
                <select value={form.status} onChange={e => onChange('status', e.target.value)}>
                  <option value="ativo">Ativo</option>
                  <option value="suspenso">Suspenso</option>
                </select>
              </div>
              <div>
                <label>Ambiente</label>
                <select value={form.ambiente} onChange={e => onChange('ambiente', e.target.value)}>
                  <option value="teste">Teste</option>
                  <option value="producao">Produção</option>
                </select>
              </div>
            </div>
          </div>

          {/* 2. Tipos de evento aceitos */}
          <div className="seg2-form-section">
            <h3>2. Tipos de evento aceitos</h3>
            <p className="seg2-muted small" style={{ marginBottom:14 }}>
              Defina quais tipos de dados esta seguradora pode enviar. Eventos com tipos não aceitos serão registrados mas não processarão recursos.
            </p>
            <div className="seg2-tiposGrid">
              {Object.entries(TIPOS_EVENTO).map(([tipo, meta]) => {
                const Icon     = meta.icon;
                const aceito   = (form.tipos_evento_aceitos || []).includes(tipo);
                return (
                  <label key={tipo} className={`seg2-tipoToggle ${aceito ? 'on' : 'off'}`}
                    style={{ borderColor: aceito ? meta.color : '#e2e8f0', background: aceito ? meta.bg : '#f8fafc' }}>
                    <input type="checkbox" checked={aceito} onChange={() => toggleTipo(tipo)} style={{ display:'none' }} />
                    <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:4 }}>
                      <Icon style={{ color: aceito ? meta.color : '#94a3b8', fontSize:13 }} />
                      <span style={{ fontWeight:800, fontSize:12, color: aceito ? meta.color : '#64748b' }}>{meta.label}</span>
                    </div>
                    <div style={{ fontSize:11, color:'#94a3b8' }}>{meta.desc}</div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* 3. Operação e canais */}
          <div className="seg2-form-section">
            <h3>3. Operação e canais</h3>
            <div className="seg2-formGrid">
              <div>
                <label>Canal de entrada (Inbound)</label>
                <select value={form.inbound_ativo} onChange={e => onChange('inbound_ativo', Number(e.target.value))}>
                  <option value={1}>Ativo</option><option value={0}>Inativo</option>
                </select>
              </div>
              <div>
                <label>Canal de saída (Outbound)</label>
                <select value={form.outbound_ativo} onChange={e => onChange('outbound_ativo', Number(e.target.value))}>
                  <option value={1}>Ativo</option><option value={0}>Inativo</option>
                </select>
              </div>
              <div>
                <label>Webhook ativo</label>
                <select value={form.webhook_ativo} onChange={e => onChange('webhook_ativo', Number(e.target.value))}>
                  <option value={1}>Ativo</option><option value={0}>Inativo</option>
                </select>
              </div>
              <div>
                <label>Método Outbound</label>
                <select value={form.metodo_outbound} onChange={e => onChange('metodo_outbound', e.target.value)}>
                  <option value="POST">POST</option><option value="PUT">PUT</option><option value="PATCH">PATCH</option>
                </select>
              </div>
              <div className="span2">
                <label>Endpoint Outbound (retorno ao concluir OS)</label>
                <input value={form.endpoint_outbound} onChange={e => onChange('endpoint_outbound', e.target.value)}
                  placeholder="https://api.parceiro.com/retorno" />
              </div>
              <div>
                <label>API Base URL</label>
                <input value={form.api_base_url} onChange={e => onChange('api_base_url', e.target.value)}
                  placeholder="https://api.parceiro.com" />
              </div>
              <div>
                <label>Timeout (ms)</label>
                <input type="number" value={form.timeout_ms} onChange={e => onChange('timeout_ms', Number(e.target.value))} />
              </div>
              <div>
                <label>Máx. tentativas retry</label>
                <input type="number" value={form.retry_max_tentativas} onChange={e => onChange('retry_max_tentativas', Number(e.target.value))} />
              </div>
              <div>
                <label>Intervalo retry (seg)</label>
                <input type="number" value={form.retry_intervalo_seg} onChange={e => onChange('retry_intervalo_seg', Number(e.target.value))} />
              </div>
            </div>
          </div>

          {/* 4. Segurança inbound */}
          <div className="seg2-form-section auth-section">
            <h3>4. Segurança de entrada (Inbound)</h3>
            <div className="seg2-formGrid">
              <div>
                <label>Método de autenticação</label>
                <select value={form.tipo_auth} onChange={e => { onChange('tipo_auth', e.target.value); onChange('webhook_secret',''); }}>
                  <option value="api_key">API Key (recomendado)</option>
                  <option value="hmac">HMAC</option>
                  <option value="none">Sem autenticação</option>
                </select>
              </div>
              <div className="span2">
                {form.tipo_auth === 'api_key' && (
                  <div className="api-key-generator">
                    <label>Chave inbound</label>
                    <p className="seg2-muted small">
                      O parceiro envia no header: <code>Authorization: Bearer CHAVE</code>
                    </p>
                    <div className="api-key-input-group">
                      <input readOnly value={form.webhook_secret || (editing ? '•••••• (oculta)' : '')}
                        placeholder="Gere uma chave abaixo" />
                      <button type="button" className="btn-generate" onClick={() => onGenSecret('inbound')}>
                        <FaSync /> Gerar
                      </button>
                      {form.webhook_secret && (
                        <button type="button" className="btn-copy" onClick={() => onCopy(form.webhook_secret, 'Chave inbound copiada!')}>
                          <FaCopy /> Copiar
                        </button>
                      )}
                    </div>
                    {editing && form.webhook_secret && (
                      <div className="warning-text">⚠ Ao salvar, a chave anterior deixará de funcionar.</div>
                    )}
                  </div>
                )}
                {form.tipo_auth === 'hmac' && (
                  <div>
                    <label>Segredo HMAC inbound</label>
                    <input value={form.webhook_secret} onChange={e => onChange('webhook_secret', e.target.value)}
                      placeholder={editing ? '•••••• (deixe vazio para manter)' : 'Segredo HMAC'} />
                  </div>
                )}
                {form.tipo_auth === 'none' && (
                  <div className="seg2-warningBox">
                    <FaExclamationTriangle />
                    <span>Modo público sem autenticação. Use apenas em ambientes controlados ou para testes.</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 5. Segurança outbound */}
          <div className="seg2-form-section auth-section">
            <h3>5. Segurança de saída (Outbound)</h3>
            <div className="seg2-formGrid">
              <div className="span2">
                <div className="api-key-generator">
                  <label>Chave outbound</label>
                  <p className="seg2-muted small">
                    Usada pelo BLB ao enviar retorno de OS/sinistro para a seguradora.
                  </p>
                  <div className="api-key-input-group">
                    <input readOnly value={form.outbound_secret || (editing ? '•••••• (oculta)' : '')}
                      placeholder="Gere uma chave abaixo" />
                    <button type="button" className="btn-generate" onClick={() => onGenSecret('outbound')}>
                      <FaSync /> Gerar
                    </button>
                    {form.outbound_secret && (
                      <button type="button" className="btn-copy" onClick={() => onCopy(form.outbound_secret, 'Chave outbound copiada!')}>
                        <FaCopy /> Copiar
                      </button>
                    )}
                  </div>
                  {editing && form.outbound_secret && (
                    <div className="warning-text">⚠ Ao salvar, a chave anterior deixará de funcionar.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 6. Email ingest */}
          <div className="seg2-form-section">
            <h3>6. Canal auxiliar por e-mail</h3>
            <div className="seg2-formGrid">
              <div>
                <label>Email ingest ativo</label>
                <select value={form.email_ingest_ativo} onChange={e => onChange('email_ingest_ativo', Number(e.target.value))}>
                  <option value={0}>Inativo</option><option value={1}>Ativo</option>
                </select>
              </div>
              <div>
                <label>Endereço de ingestão</label>
                <input value={form.email_ingest_address} onChange={e => onChange('email_ingest_address', e.target.value)}
                  placeholder="integracao@empresa.com" />
              </div>
            </div>
          </div>
        </div>

        <div className="seg2-modalFooter">
          <button className="seg2-btn seg2-btnGhost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="seg2-btn primary" onClick={onSave} disabled={saving}>
            <FaCheckCircle /> {saving ? 'Salvando...' : 'Salvar Integração'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════

export default function IntegracoesSeguradoras({ user }) {
  const socket = useSocket();

  const [loading,    setLoading]    = useState(true);
  const [errMsg,     setErrMsg]     = useState('');
  const [activeTab,  setActiveTab]  = useState('painel');
  const [seguradoras,setSeguradoras]= useState([]);
  const [eventos,    setEventos]    = useState([]);
  const [sinistrosMap, setSinistrosMap] = useState({}); // segId → count
  const [apolicesMap,  setApolicesMap]  = useState({}); // segId → count

  // Filtros de log
  const [fSeg,    setFSeg]    = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fDir,    setFDir]    = useState('');
  const [fTipo,   setFTipo]   = useState('');
  const [search,  setSearch]  = useState('');
  const [limit,   setLimit]   = useState(100);

  // Drawer
  const [selEvento, setSelEvento] = useState(null);
  const [drawerOpen,setDrawerOpen]= useState(false);

  // Modal seguradora
  const isAdminGeral = user?.perfil === 'admin_geral';
  const [segModalOpen, setSegModalOpen] = useState(false);
  const [editingSeg,   setEditingSeg]   = useState(null);
  const [segForm,      setSegForm]      = useState(emptySeg);
  const [saving,       setSaving]       = useState(false);

  const webhookBase = useMemo(() => { try { return window.location.origin; } catch { return ''; } }, []);

  // ── Carregamento ──────────────────────────────────────────────
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setErrMsg('');
    try {
      const [rs, re] = await Promise.all([
        safeGet('/api/integracoes/seguradoras'),
        safeGet(`/api/integracoes/seguradoras/eventos?limit=${limit}`),
      ]);
      const segs = Array.isArray(rs.data) ? rs.data : [];
      const evts = Array.isArray(re.data) ? re.data : [];
      setSeguradoras(segs);
      setEventos(evts);

      // Busca contagens de sinistros e apólices por seguradora (paralelo, ignora erros)
      const counts = await Promise.allSettled(segs.map(async (s) => {
        const [rsin, rapl] = await Promise.allSettled([
          safeGet(`/api/integracoes/seguradoras/${s.id}/sinistros`),
          safeGet(`/api/integracoes/seguradoras/${s.id}/apolices`),
        ]);
        return {
          id: s.id,
          sinistros: rsin.status === 'fulfilled' ? (Array.isArray(rsin.value?.data) ? rsin.value.data.length : 0) : 0,
          apolices:  rapl.status === 'fulfilled' ? (Array.isArray(rapl.value?.data) ? rapl.value.data.length : 0) : 0,
        };
      }));
      const sm = {}, am = {};
      counts.forEach(r => { if (r.status === 'fulfilled') { sm[r.value.id] = r.value.sinistros; am[r.value.id] = r.value.apolices; } });
      setSinistrosMap(sm);
      setApolicesMap(am);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Falha ao carregar.';
      setErrMsg(msg);
      if (!silent) toast.error(msg);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [limit]);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    if (!socket) return;
    const fn = () => load(true);
    socket.on('seguradoras:webhook', fn);
    socket.on('seguradoras:evento',  fn);
    return () => { socket.off('seguradoras:webhook', fn); socket.off('seguradoras:evento', fn); };
  }, [socket, load]);

  // ── Actions ───────────────────────────────────────────────────
  const copiar = async (text, label = 'Copiado!') => {
    try { await navigator.clipboard.writeText(String(text||'')); toast.success(label); }
    catch { toast.info('Selecione e copie manualmente.'); }
  };

  const testar = async (id, tipo = 'chamado') => {
    try {
      await safePost(`/api/integracoes/seguradoras/${id}/test`, { tipo_evento: tipo });
      toast.success(`Teste de "${tipo}" processado! Veja nos logs.`);
      setActiveTab('logs');
      await load(true);
    } catch (err) { toast.error(err?.response?.data?.error || 'Falha no teste.'); }
  };

  const reprocessar = async (eventoId) => {
    try {
      await safePost(`/api/integracoes/seguradoras/eventos/${eventoId}/reprocessar`, {});
      toast.success('Evento reprocessado!');
      await load(true);
      if (selEvento?.id === eventoId) { setDrawerOpen(false); setTimeout(() => setSelEvento(null), 200); }
    } catch (err) { toast.error(err?.response?.data?.error || 'Falha ao reprocessar.'); }
  };

  const quickToggle = async (seg, field) => {
    try {
      const val = field === 'status'
        ? (String(seg.status).toLowerCase() === 'ativo' ? 'suspenso' : 'ativo')
        : Number(seg[field] ? 0 : 1);
      await safePut(`/api/integracoes/seguradoras/${seg.id}`, { [field]: val });
      toast.success('Atualizado.');
      await load(true);
    } catch (err) { toast.error(err?.response?.data?.error || 'Falha ao atualizar.'); }
  };

  const openNewSeg  = () => { setEditingSeg(null); setSegForm(emptySeg); setSegModalOpen(true); };
  const openEditSeg = (seg) => {
    setEditingSeg(seg);
    let tiposAceitos = [];
    try {
      tiposAceitos = typeof seg.tipos_evento_aceitos === 'string'
        ? JSON.parse(seg.tipos_evento_aceitos) : (seg.tipos_evento_aceitos || []);
    } catch { tiposAceitos = Object.keys(TIPOS_EVENTO); }
    setSegForm({
      nome: seg.nome||'', slug: seg.slug||'', codigo_interno: seg.codigo_interno||'',
      adapter_key: seg.adapter_key || (seg.slug||'').replace(/-/g,'_'),
      status: seg.status||'ativo', tipo_auth: seg.tipo_auth||'api_key', ambiente: seg.ambiente||'teste',
      api_base_url: seg.api_base_url||'', endpoint_outbound: seg.endpoint_outbound||'',
      metodo_outbound: seg.metodo_outbound||'POST', timeout_ms: seg.timeout_ms||15000,
      retry_max_tentativas: seg.retry_max_tentativas||3, retry_intervalo_seg: seg.retry_intervalo_seg||300,
      webhook_ativo: Number(seg.webhook_ativo?1:0), inbound_ativo: Number(seg.inbound_ativo?1:0),
      outbound_ativo: Number(seg.outbound_ativo?1:0), email_ingest_ativo: Number(seg.email_ingest_ativo?1:0),
      email_ingest_address: seg.email_ingest_address||'',
      webhook_secret:'', outbound_secret:'',
      tipos_evento_aceitos: tiposAceitos,
    });
    setSegModalOpen(true);
  };

  const handleFormChange = (key, value) => setSegForm(p => ({ ...p, [key]: value }));

  const genSecret = (scope = 'inbound') => {
    const k = `sk_live_${Math.random().toString(36).slice(2,15)}${Math.random().toString(36).slice(2,15)}`;
    handleFormChange(scope === 'outbound' ? 'outbound_secret' : 'webhook_secret', k);
  };

  const saveSeg = async () => {
    if (!segForm.nome || !segForm.slug) { toast.warn('Nome e slug são obrigatórios.'); return; }
    setSaving(true);
    try {
      const payload = {
        ...segForm,
        slug: String(segForm.slug||'').trim().toLowerCase().replace(/\s+/g,'-'),
        adapter_key: segForm.adapter_key || String(segForm.slug||'').trim().toLowerCase().replace(/-/g,'_'),
        timeout_ms: Number(segForm.timeout_ms||15000),
        retry_max_tentativas: Number(segForm.retry_max_tentativas||3),
        retry_intervalo_seg: Number(segForm.retry_intervalo_seg||300),
        webhook_ativo: Number(segForm.webhook_ativo?1:0),
        inbound_ativo: Number(segForm.inbound_ativo?1:0),
        outbound_ativo: Number(segForm.outbound_ativo?1:0),
        email_ingest_ativo: Number(segForm.email_ingest_ativo?1:0),
        tipos_evento_aceitos: segForm.tipos_evento_aceitos,
      };
      if (!payload.webhook_secret)       delete payload.webhook_secret;
      if (!payload.outbound_secret)      delete payload.outbound_secret;
      if (!payload.email_ingest_address) payload.email_ingest_address = null;
      if (!payload.api_base_url)         payload.api_base_url = null;
      if (!payload.endpoint_outbound)    payload.endpoint_outbound = null;
      if (!payload.codigo_interno)       payload.codigo_interno = null;

      if (editingSeg?.id) {
        await safePut(`/api/integracoes/seguradoras/${editingSeg.id}`, payload);
        toast.success('Integração atualizada!');
      } else {
        await safePost('/api/integracoes/seguradoras', payload);
        toast.success('Integração criada!');
      }
      setSegModalOpen(false);
      await load(true);
    } catch (err) { toast.error(err?.response?.data?.error || 'Falha ao salvar.'); }
    finally { setSaving(false); }
  };

  const deleteSeg = async (seg) => {
    if (!window.confirm(`Excluir "${seg.nome}"?`)) return;
    try {
      await safeDel(`/api/integracoes/seguradoras/${seg.id}`);
      toast.success('Integração excluída.');
      await load(true);
    } catch (err) { toast.error(err?.response?.data?.error || 'Falha ao excluir.'); }
  };

  // ── Derivações ────────────────────────────────────────────────
  const partnerStats = useMemo(() => {
    const map = {};
    seguradoras.forEach(s => {
      map[s.id] = { total:0, inbound:0, outbound:0, processado:0, erro:0, rejeitado:0, recebido:0,
        ultimoEvento:null, ultimoErro:null, porTipo:{} };
    });
    eventos.forEach(ev => {
      if (!map[ev.seguradora_id]) {
        map[ev.seguradora_id] = { total:0, inbound:0, outbound:0, processado:0, erro:0, rejeitado:0, recebido:0,
          ultimoEvento:null, ultimoErro:null, porTipo:{} };
      }
      const s = map[ev.seguradora_id];
      s.total++;
      const dir = String(ev.direcao||'').toLowerCase();
      if (dir === 'inbound')  s.inbound++;
      if (dir === 'outbound') s.outbound++;
      const st = String(ev.status||'').toLowerCase();
      if (st === 'processado') s.processado++;
      if (st === 'erro')       s.erro++;
      if (st === 'rejeitado')  s.rejeitado++;
      if (st === 'recebido')   s.recebido++;
      const tipo = String(ev.tipo_evento||'outro').toLowerCase();
      s.porTipo[tipo] = (s.porTipo[tipo]||0) + 1;
      const d = ev.recebido_em || ev.created_at;
      if (!s.ultimoEvento || new Date(d) > new Date(s.ultimoEvento)) s.ultimoEvento = d;
      if ((st === 'erro' || st === 'rejeitado') && (!s.ultimoErro || new Date(d) > new Date(s.ultimoErro))) s.ultimoErro = d;
    });
    return map;
  }, [seguradoras, eventos]);

  const metrics = useMemo(() => ({
    total:     eventos.length,
    inbound:   eventos.filter(e => String(e.direcao||'').toLowerCase() === 'inbound').length,
    outbound:  eventos.filter(e => String(e.direcao||'').toLowerCase() === 'outbound').length,
    ok:        eventos.filter(e => String(e.status||'').toLowerCase() === 'processado').length,
    bad:       eventos.filter(e => ['erro','rejeitado'].includes(String(e.status||'').toLowerCase())).length,
    pendentes: eventos.filter(e => String(e.status||'').toLowerCase() === 'recebido').length,
    last:      eventos[0]?.recebido_em || eventos[0]?.created_at || null,
    porTipo:   Object.fromEntries(Object.keys(TIPOS_EVENTO).map(t => [
      t, eventos.filter(e => String(e.tipo_evento||'').toLowerCase() === t).length
    ])),
  }), [eventos]);

  const filteredEventos = useMemo(() => {
    const q = search.trim().toLowerCase();
    return eventos.filter(ev => {
      if (fSeg    && String(ev.seguradora_id) !== String(fSeg))               return false;
      if (fStatus && String(ev.status||'').toLowerCase() !== fStatus)         return false;
      if (fDir    && String(ev.direcao||'').toLowerCase() !== fDir)           return false;
      if (fTipo   && String(ev.tipo_evento||'').toLowerCase() !== fTipo)      return false;
      if (!q) return true;
      return [ev.id, ev.seguradora_nome, ev.tipo_evento, ev.status, ev.protocolo_externo,
        ev.numero_sinistro, ev.os_id, ev.sinistro_id, ev.erro, ev.payload_json, ev.response_json]
        .join(' ').toLowerCase().includes(q);
    });
  }, [eventos, fSeg, fStatus, fDir, fTipo, search]);

  const tiposDisp = useMemo(() => [...new Set(eventos.map(e => e.tipo_evento).filter(Boolean))], [eventos]);

  return (
    <div className="seg2-page">
      {/* Header */}
      <div className="seg2-topbar">
        <div>
          <h1>Integrações · Seguradoras</h1>
          <p className="seg2-subtitle">
            Hub bidirecional: recebe chamados, sinistros e apólices · retorna status e conclusões
          </p>
        </div>
        <div className="seg2-actions">
          <button className="seg2-btn seg2-btnGhost" onClick={() => load(false)}><FaSync /> Atualizar</button>
          {isAdminGeral && <button className="seg2-btn" onClick={openNewSeg}><FaPlus /> Nova Integração</button>}
        </div>
      </div>

      {errMsg && (
        <div className="seg2-alert">
          <FaExclamationTriangle /><span>{errMsg}</span>
          <button className="seg2-alertClose" onClick={() => setErrMsg('')}><FaTimes /></button>
        </div>
      )}

      {/* KPIs globais */}
      <div className="seg2-kpis">
        <div className="seg2-kpiCard">
          <div className="seg2-kpiIcon neutral"><FaServer /></div>
          <div>
            <div className="seg2-kpiValue">{seguradoras.length}</div>
            <div className="seg2-kpiLabel">Parceiros Configurados</div>
            <div className="seg2-kpiSub">{seguradoras.filter(s => String(s.status).toLowerCase() === 'ativo').length} ativo(s)</div>
          </div>
        </div>
        <div className="seg2-kpiCard">
          <div className="seg2-kpiIcon ok"><FaCheckCircle /></div>
          <div>
            <div className="seg2-kpiValue">{metrics.ok}</div>
            <div className="seg2-kpiLabel">Processados</div>
            <div className="seg2-kpiSub">{metrics.inbound} entrada · {metrics.outbound} saída</div>
          </div>
        </div>
        <div className="seg2-kpiCard">
          <div className="seg2-kpiIcon bad"><FaExclamationTriangle /></div>
          <div>
            <div className="seg2-kpiValue">{metrics.bad}</div>
            <div className="seg2-kpiLabel">Falhas / Rejeições</div>
            <div className="seg2-kpiSub">{metrics.pendentes} pendente(s)</div>
          </div>
        </div>
        <div className="seg2-kpiCard">
          <div className="seg2-kpiIcon neutral"><FaClock /></div>
          <div>
            <div className="seg2-kpiValue">{metrics.total}</div>
            <div className="seg2-kpiLabel">Total de Eventos</div>
            <div className="seg2-kpiSub">{metrics.last ? relTime(metrics.last) : 'Sem tráfego'}</div>
          </div>
        </div>
      </div>

      {/* Breakdown por tipo (global) */}
      {metrics.total > 0 && (
        <div className="seg2-globalTipos">
          {Object.entries(TIPOS_EVENTO).map(([tipo, meta]) => {
            const Icon  = meta.icon;
            const count = metrics.porTipo[tipo] || 0;
            return (
              <div key={tipo} className="seg2-globalTipoChip" style={{ borderColor: meta.color }}>
                <Icon style={{ color: meta.color, fontSize: 12 }} />
                <span style={{ fontWeight: 800, color: '#0f172a' }}>{count}</span>
                <span style={{ fontSize: 11, color: '#64748b' }}>{meta.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="seg2-tabs">
        {[
          { id:'painel',    label:'Painel Operacional',     icon: FaServer   },
          { id:'parceiros', label:'Parceiros & Configuração',icon: FaShieldAlt },
          { id:'logs',      label:'Tráfego & Logs',          icon: FaListUl   },
        ].map(t => (
          <button key={t.id} className={`seg2-tab ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}>
            <t.icon /> {t.label}
          </button>
        ))}
      </div>

      <div className="seg2-tab-content">
        {/* ─── ABA 1: PAINEL ─────────────────────────────────────── */}
        {activeTab === 'painel' && (
          <div className="animate-fade-in">
            <div className="seg2-partnerGrid">
              {seguradoras.map(seg => (
                <PartnerCard key={seg.id} seg={seg}
                  stat={partnerStats[seg.id] || {}}
                  isAdminGeral={isAdminGeral}
                  sinistrosRecebidos={sinistrosMap[seg.id] || 0}
                  apolicesRecebidas={apolicesMap[seg.id] || 0}
                  onTest={testar} onEdit={openEditSeg}
                  onToggle={quickToggle} onCopy={copiar}
                  webhookBase={webhookBase} />
              ))}
              {!loading && seguradoras.length === 0 && (
                <div className="seg2-emptyCard">
                  <FaServer size={28} />
                  <h3>Nenhuma integração cadastrada</h3>
                  <p>Cadastre Allianz, Porto Seguro ou outros parceiros para iniciar o hub bidirecional.</p>
                  {isAdminGeral && <button className="seg2-btn" onClick={openNewSeg}><FaPlus /> Nova Integração</button>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── ABA 2: PARCEIROS ──────────────────────────────────── */}
        {activeTab === 'parceiros' && (
          <section className="seg2-card animate-fade-in">
            <div className="seg2-tableWrap">
              <table className="seg2-table">
                <thead>
                  <tr>
                    <th>Parceiro</th>
                    <th>Tipos Aceitos</th>
                    <th>Entrada</th>
                    <th>Saída</th>
                    <th>Sinistros</th>
                    <th>Apólices</th>
                    <th>Status</th>
                    <th style={{ textAlign:'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {seguradoras.map(s => {
                    let tiposAceitos = [];
                    try { tiposAceitos = typeof s.tipos_evento_aceitos === 'string' ? JSON.parse(s.tipos_evento_aceitos) : (s.tipos_evento_aceitos||[]); } catch {}
                    return (
                      <tr key={s.id}>
                        <td>
                          <div className="seg2-strong">{s.nome}</div>
                          <div className="seg2-muted">slug: {s.slug} · adapter: {s.adapter_key||s.slug} · {s.ambiente}</div>
                        </td>
                        <td>
                          <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                            {tiposAceitos.length > 0
                              ? tiposAceitos.map(t => <TipoBadge key={t} tipo={t} />)
                              : <span className="seg2-muted">Todos</span>
                            }
                          </div>
                        </td>
                        <td>
                          <div className="seg2-cellStack">
                            <span className={pillClass(Number(s.inbound_ativo)?'ativo':'suspenso')}>{boolText(s.inbound_ativo)}</span>
                            <small>{authLabel(s.tipo_auth)}</small>
                          </div>
                        </td>
                        <td>
                          <div className="seg2-cellStack">
                            <span className={pillClass(Number(s.outbound_ativo)?'ativo':'suspenso')}>{boolText(s.outbound_ativo)}</span>
                            <small>{s.endpoint_outbound ? 'Endpoint config.' : 'Sem endpoint'}</small>
                          </div>
                        </td>
                        <td>
                          <span style={{ fontWeight:900, color:'#dc2626', fontSize:15 }}>{sinistrosMap[s.id]||0}</span>
                        </td>
                        <td>
                          <span style={{ fontWeight:900, color:'#16a34a', fontSize:15 }}>{apolicesMap[s.id]||0}</span>
                        </td>
                        <td><span className={pillClass(s.status)}>{s.status}</span></td>
                        <td className="seg2-actionsCol" style={{ justifyContent:'flex-end' }}>
                          <button className="seg2-miniBtn" onClick={() => testar(s.id,'chamado')} title="Testar chamado"><FaBolt /> Testar</button>
                          {isAdminGeral && <>
                            <button className="seg2-miniBtn seg2-miniBtnGhost" onClick={() => openEditSeg(s)}><FaCogs /> Editar</button>
                            <button className="seg2-miniBtn seg2-miniBtnDanger" onClick={() => deleteSeg(s)}><FaTimes /> Excluir</button>
                          </>}
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && seguradoras.length === 0 && (
                    <tr><td colSpan="8" className="seg2-empty">Nenhum parceiro configurado.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* ─── ABA 3: LOGS ───────────────────────────────────────── */}
        {activeTab === 'logs' && (
          <section className="seg2-card animate-fade-in">
            <div className="seg2-filters">
              <div className="seg2-search">
                <FaSearch />
                <input placeholder="Buscar por protocolo, OS, sinistro, payload, erro…"
                  value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <div className="seg2-filterGroup">
                <FaFilter className="seg2-filterIcon" />
                <select value={fSeg} onChange={e => setFSeg(e.target.value)}>
                  <option value="">Todas seguradoras</option>
                  {seguradoras.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
                <select value={fDir} onChange={e => setFDir(e.target.value)}>
                  <option value="">Todas direções</option>
                  <option value="inbound">Entrada</option>
                  <option value="outbound">Saída</option>
                </select>
                <select value={fTipo} onChange={e => setFTipo(e.target.value)}>
                  <option value="">Todos os tipos</option>
                  {Object.entries(TIPOS_EVENTO).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select value={fStatus} onChange={e => setFStatus(e.target.value)}>
                  <option value="">Todos os status</option>
                  <option value="recebido">Recebido</option>
                  <option value="processado">Processado</option>
                  <option value="erro">Erro</option>
                  <option value="rejeitado">Rejeitado</option>
                  <option value="duplicado">Duplicado</option>
                </select>
                <select value={limit} onChange={e => setLimit(Number(e.target.value))}>
                  <option value={50}>Últimos 50</option>
                  <option value={100}>Últimos 100</option>
                  <option value={200}>Últimos 200</option>
                  <option value={250}>Últimos 250</option>
                </select>
              </div>
            </div>

            <div className="seg2-logsSummary">
              <span className="seg2-summaryChip">Filtrados: <b>{filteredEventos.length}</b></span>
              <span className="seg2-summaryChip">Entrada: <b>{filteredEventos.filter(e => String(e.direcao).toLowerCase()==='inbound').length}</b></span>
              <span className="seg2-summaryChip">Saída: <b>{filteredEventos.filter(e => String(e.direcao).toLowerCase()==='outbound').length}</b></span>
              <span className="seg2-summaryChip error">Falhas: <b>{filteredEventos.filter(e => ['erro','rejeitado'].includes(String(e.status).toLowerCase())).length}</b></span>
            </div>

            <div className="seg2-tableWrap">
              <table className="seg2-table logs-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Tipo de Evento</th>
                    <th>Direção</th>
                    <th>Seguradora</th>
                    <th>Status</th>
                    <th>Vínculos</th>
                    <th>Data/Hora</th>
                    <th style={{ textAlign:'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEventos.map(ev => {
                    const podeReproc = String(ev.direcao||'').toLowerCase() === 'inbound'
                      && ['erro','rejeitado'].includes(String(ev.status||'').toLowerCase());
                    return (
                      <tr key={ev.id}>
                        <td><code style={{ fontFamily:'monospace', fontSize:12 }}>#{ev.id}</code></td>
                        <td><TipoBadge tipo={ev.tipo_evento} /></td>
                        <td><span className={dirBadgeCls(ev.direcao)}>{dirLabel(ev.direcao)}</span></td>
                        <td>
                          <div className="seg2-strong">{ev.seguradora_nome||'—'}</div>
                          <div className="seg2-muted">{ev.origem_evento||'—'}</div>
                        </td>
                        <td><span className={pillClass(ev.status)}>{ev.status}</span></td>
                        <td>
                          <div className="seg2-cellStack">
                            {ev.protocolo_externo && <span style={{ fontFamily:'monospace', fontSize:11 }}>{ev.protocolo_externo}</span>}
                            {ev.os_id        && <span style={{ color:'#6366f1', fontWeight:700, fontSize:11 }}>OS #{ev.os_id}</span>}
                            {ev.sinistro_id  && <span style={{ color:'#dc2626', fontWeight:700, fontSize:11 }}>Sin. #{ev.sinistro_id}</span>}
                            {ev.numero_sinistro && <span style={{ color:'#ea580c', fontSize:11 }}>#{ev.numero_sinistro}</span>}
                            {!ev.os_id && !ev.sinistro_id && !ev.protocolo_externo && <span className="seg2-muted">—</span>}
                          </div>
                        </td>
                        <td style={{ fontSize:12, color:'#64748b' }}>{fmtDate(ev.recebido_em||ev.created_at)}</td>
                        <td className="seg2-actionsCol" style={{ justifyContent:'flex-end' }}>
                          <button className="seg2-miniBtn seg2-miniBtnGhost"
                            onClick={() => { setSelEvento(ev); setDrawerOpen(true); }}>Detalhes</button>
                          {podeReproc && (
                            <button className="seg2-miniBtn" onClick={() => reprocessar(ev.id)}>
                              <FaSync /> Reprocessar
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && filteredEventos.length === 0 && (
                    <tr><td colSpan="8" className="seg2-empty">Nenhum evento encontrado com os filtros aplicados.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      {/* Drawer de evento */}
      <EventDrawer ev={selEvento} open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setTimeout(() => setSelEvento(null), 200); }}
        onReprocess={reprocessar} />

      {/* Modal seguradora */}
      <SeguradoraModal
        open={segModalOpen} editing={editingSeg} form={segForm}
        onChange={handleFormChange} onSave={saveSeg}
        onClose={() => setSegModalOpen(false)} saving={saving}
        onGenSecret={genSecret} onCopy={copiar} />
    </div>
  );
}